// Permission Slip Type Definitions
// Comprehensive types to replace 'any' types in permission slip services

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'email'
  | 'phone'
  | 'date'
  | 'time'
  | 'datetime'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'file'
  | 'signature'
  | 'address'
  | 'url'
  | 'currency'
  | 'file_upload'
  | 'student_info'
  | 'parent_info'
  | 'consent_text';

// Constants for component compatibility
export const FieldTypeEnum = {
  TEXT: 'text' as const,
  TEXTAREA: 'textarea' as const, 
  NUMBER: 'number' as const,
  EMAIL: 'email' as const,
  PHONE: 'phone' as const,
  DATE: 'date' as const,
  TIME: 'time' as const,
  DATETIME: 'datetime' as const,
  SELECT: 'select' as const,
  RADIO: 'radio' as const,
  CHECKBOX: 'checkbox' as const,
  FILE: 'file' as const,
  SIGNATURE: 'signature' as const,
  ADDRESS: 'address' as const,
  URL: 'url' as const,
  CURRENCY: 'currency' as const,
  FILE_UPLOAD: 'file_upload' as const,
  STUDENT_INFO: 'student_info' as const,
  PARENT_INFO: 'parent_info' as const,
  CONSENT_TEXT: 'consent_text' as const
} as const;

// For backward compatibility, re-export FieldTypeEnum as FieldType
export { FieldTypeEnum as FieldType };

export type FormStatus = 
  | 'draft'
  | 'active'
  | 'inactive'
  | 'archived'
  | 'template';

export const FormStatusEnum = {
  DRAFT: 'draft' as const,
  ACTIVE: 'active' as const,
  INACTIVE: 'inactive' as const,
  ARCHIVED: 'archived' as const,
  TEMPLATE: 'template' as const
} as const;

// For backward compatibility, re-export FormStatusEnum as FormStatus  
export { FormStatusEnum as FormStatus };

export type ResponseStatus =
  | 'pending'
  | 'completed'
  | 'rejected'
  | 'expired'
  | 'withdrawn';

// Validation Rule Types (replacing Record<string, any>)
export interface ValidationRule {
  pattern?: string;
  min_length?: number;
  max_length?: number;
  min_value?: number;
  max_value?: number;
  required_if?: ConditionalRule;
  custom_validator?: string;
  error_message?: string;
}

// Conditional Logic Types (replacing Record<string, any>)
export interface ConditionalRule {
  field_name: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than' | 'is_empty' | 'is_not_empty';
  value: string | number | boolean | null;
  logical_operator?: 'AND' | 'OR';
}

export interface ConditionalLogic {
  show_if?: ConditionalRule[];
  hide_if?: ConditionalRule[];
  required_if?: ConditionalRule[];
}

// Field Value Types (replacing any for default_value)
export type FieldValue = 
  | string 
  | number 
  | boolean 
  | Date 
  | File 
  | string[] 
  | number[]
  | null;

// Field Metadata Types (replacing Record<string, any>)
export interface FieldMetadata {
  column_span?: number;
  row_span?: number;
  css_class?: string;
  custom_attributes?: Record<string, string>;
  accessibility_label?: string;
  tab_index?: number;
  auto_focus?: boolean;
  readonly?: boolean;
  step?: number; // for number inputs
  multiple?: boolean; // for file/select inputs
  accept?: string; // for file inputs
  max_file_size?: number; // in bytes
  allowed_extensions?: string[];
}

// Form Template Data Types (replacing any in createFormFromTemplate)
export interface FormTemplateData {
  title: string;
  description?: string;
  school_id?: number;
  due_date?: string;
  is_active?: boolean;
  category?: string;
  instructions?: string;
  field_overrides?: FieldOverride[];
  custom_css?: string;
  email_notifications?: EmailNotificationSettings;
}

export interface FieldOverride {
  field_name: string;
  new_label?: string;
  new_description?: string;
  new_required?: boolean;
  new_default_value?: FieldValue;
  new_validation_rules?: ValidationRule;
}

// Notification Settings
export interface EmailNotificationSettings {
  send_confirmation?: boolean;
  send_reminder?: boolean;
  reminder_days_before?: number;
  custom_message?: string;
  cc_emails?: string[];
  bcc_emails?: string[];
}

// Response Data Types
export interface ResponseData {
  field_responses: FieldResponse[];
  metadata?: ResponseMetadata;
  attachments?: FileAttachment[];
  signature_data?: SignatureData;
  submission_timestamp?: string;
  ip_address?: string;
  user_agent?: string;
}

export interface FieldResponse {
  field_id: number;
  field_name: string;
  value: FieldValue;
  file_uploads?: FileAttachment[];
}

export interface ResponseMetadata {
  form_version: string;
  completion_time_seconds?: number;
  device_type?: 'desktop' | 'tablet' | 'mobile';
  browser?: string;
  referrer?: string;
}

export interface FileAttachment {
  id?: number;
  filename: string;
  original_filename: string;
  file_size: number;
  content_type: string;
  upload_path: string;
  checksum?: string;
  virus_scan_status?: 'pending' | 'clean' | 'infected' | 'error';
  uploaded_at?: string;
}

export interface SignatureData {
  signature_svg: string;
  signature_timestamp: string;
  signer_name: string;
  signer_ip?: string;
}

// Analytics and Reporting Types
export interface FormAnalytics {
  total_responses: number;
  completion_rate: number;
  average_completion_time: number;
  response_trends: ResponseTrend[];
  field_analytics: FieldAnalytics[];
  demographic_breakdown?: DemographicBreakdown;
}

export interface ResponseTrend {
  date: string;
  count: number;
  completion_rate: number;
}

export interface FieldAnalytics {
  field_id: number;
  field_name: string;
  completion_rate: number;
  most_common_values?: Array<{ value: string; count: number }>;
  validation_errors: number;
  skip_rate: number;
}

export interface DemographicBreakdown {
  by_grade?: Record<string, number>;
  by_school?: Record<string, number>;
  by_demographic?: Record<string, number>;
}

// Bulk Operations Types
export interface BulkOperationRequest {
  operation: 'approve' | 'reject' | 'archive' | 'delete' | 'send_reminder';
  response_ids: number[];
  reason?: string;
  custom_message?: string;
  schedule_for?: string; // ISO date string
}

export interface BulkOperationResult {
  successful: number[];
  failed: Array<{ id: number; error: string }>;
  total_processed: number;
  operation_id: string;
}

// Distribution Settings Types
export interface DistributionSettings {
  distribution_method: 'email' | 'sms' | 'both' | 'portal_only';
  target_audience: TargetAudience;
  schedule?: DistributionSchedule;
  reminders?: ReminderSettings;
  custom_message?: string;
  attachments?: number[]; // File IDs
}

export interface TargetAudience {
  include_all_students?: boolean;
  grade_levels?: string[];
  schools?: number[];
  student_groups?: string[];
  exclude_ids?: number[];
  custom_filters?: AudienceFilter[];
}

export interface AudienceFilter {
  field: string;
  operator: 'equals' | 'contains' | 'in' | 'not_in';
  value: string | string[];
}

export interface DistributionSchedule {
  send_immediately?: boolean;
  scheduled_date?: string;
  recurring?: RecurringSettings;
}

export interface RecurringSettings {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  end_date?: string;
  max_occurrences?: number;
}

export interface ReminderSettings {
  enabled: boolean;
  days_before_due: number[];
  custom_reminder_message?: string;
  send_to_incomplete_only: boolean;
}

// Compliance and Legal Types
export interface ComplianceSettings {
  data_retention_days: number;
  require_parent_signature: boolean;
  require_student_signature: boolean;
  ferpa_compliant: boolean;
  coppa_compliant: boolean;
  custom_legal_text?: string;
  audit_trail_enabled: boolean;
}

export interface AuditTrailEntry {
  id: number;
  action: string;
  user_id: number;
  user_name: string;
  timestamp: string;
  details: Record<string, FieldValue>;
  ip_address?: string;
  user_agent?: string;
}

// API Request/Response Types
export interface CreateFormRequest {
  title: string;
  description?: string;
  category?: string;
  due_date?: string;
  is_template?: boolean;
  school_ids?: number[];
  compliance_settings?: ComplianceSettings;
  fields?: Partial<PermissionSlipField>[];
}

export interface UpdateFormRequest extends Partial<CreateFormRequest> {
  status?: FormStatus;
  instructions?: string;
}

export interface FormListQuery {
  skip?: number;
  limit?: number;
  status?: FormStatus[];
  category?: string;
  is_template?: boolean;
  school_id?: number;
  search?: string;
  created_after?: string;
  created_before?: string;
  sort_by?: 'created_at' | 'updated_at' | 'title' | 'due_date';
  sort_order?: 'asc' | 'desc';
}

// Re-export enhanced interfaces
export interface PermissionSlipField {
  id: number;
  form_id: number;
  field_type: FieldType;
  label: string;
  name: string;
  description?: string;
  placeholder?: string;
  help_text?: string;
  default_value?: FieldValue;
  is_required: boolean;
  is_visible: boolean;
  display_order: number;
  validation_rules?: ValidationRule;
  conditional_logic?: ConditionalLogic;
  options?: FieldOption[];
  metadata?: FieldMetadata;
  created_at: string;
  updated_at: string;
}

export interface FieldOption {
  value: string;
  label: string;
  description?: string;
  display_order?: number;
  selected?: boolean;
  conditional_logic?: ConditionalLogic;
}

export interface PermissionSlipForm {
  id: number;
  district_id: number;
  title: string;
  description?: string;
  category?: string;
  status: FormStatus;
  is_template: boolean;
  created_by: number;
  school_ids?: number[]; // Made optional for backward compatibility
  due_date?: string;
  instructions?: string;
  fields?: PermissionSlipField[]; // Made optional for backward compatibility
  distribution_settings?: DistributionSettings;
  compliance_settings?: ComplianceSettings;
  custom_css?: string;
  metadata?: FieldMetadata;
  created_at: string;
  updated_at: string;
  total_responses?: number;
  completion_rate?: number;
  // Legacy fields for backward compatibility
  version?: number;
  requires_payment?: boolean;
  payment_amount?: number;
  payment_instructions?: string;
  effective_date?: string;
  expiration_date?: string;
}

export interface PermissionSlipResponse {
  id: number;
  form_id: number;
  student_id: number;
  parent_id?: number;
  status: ResponseStatus;
  response_data: ResponseData;
  submitted_at?: string;
  reviewed_at?: string;
  reviewed_by?: number;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
}

// Analytics and Reporting Types
export interface DistributionAnalytics {
  total_distributed: number;
  total_submitted: number;
  completion_rate: number;
  by_school: Array<{
    school_id: number;
    school_name: string;
    distributed: number;
    submitted: number;
    completion_rate: number;
  }>;
  by_grade: Array<{
    grade: string;
    distributed: number;
    submitted: number;
    completion_rate: number;
  }>;
  timeline: Array<{
    date: string;
    distributed_count: number;
    submission_count: number;
  }>;
}

export interface NotificationTriggerResult {
  notification_id: string;
  recipients_count: number;
  estimated_delivery_time: string;
  message: string;
}

export interface ReminderScheduleResult {
  schedule_id: string;
  reminders_count: number;
  next_reminder_date: string;
  recipients_count: number;
}

export interface NotificationStatus {
  form_id: number;
  notifications_sent: number;
  pending_notifications: number;
  failed_notifications: number;
  last_notification_date?: string;
  next_scheduled_date?: string;
  delivery_status: Array<{
    notification_id: string;
    recipient_email: string;
    status: 'sent' | 'delivered' | 'opened' | 'failed';
    sent_at: string;
    opened_at?: string;
  }>;
}

export type SubmissionStatus = 'pending' | 'submitted' | 'approved' | 'rejected' | 'expired';

export interface ExportResult {
  filename: string;
  download_url: string;
  export_id: string;
  created_at: string;
  expires_at: string;
  file_size: number;
  records_count: number;
}

export interface ComplianceReport {
  report_id: string;
  generated_at: string;
  total_forms: number;
  compliance_summary: {
    compliant_count: number;
    non_compliant_count: number;
    pending_count: number;
    overall_compliance_rate: number;
  };
  by_school: Array<{
    school_id: number;
    school_name: string;
    compliance_rate: number;
    total_students: number;
    compliant_students: number;
  }>;
  by_form: Array<{
    form_id: number;
    form_title: string;
    compliance_rate: number;
    total_distributed: number;
    total_submitted: number;
  }>;
}

export interface SearchResults {
  forms: PermissionSlipForm[];
  responses: PermissionSlipResponse[];
  total_count: number;
  page: number;
  per_page: number;
  filters_applied: Record<string, unknown>;
  search_query: string;
}

// Export types for use in other files
export * from './index'; // Re-export common types like ApiResponse, PaginatedResponse 